import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useNetworkLearning } from '@/hooks/useNetworkLearning';
import { getSignalLearningAdjustment, type LearningPattern } from '@/lib/enterprise/learningWeights';
import { computeOutcomeAdjustment } from '@/lib/enterprise/outcomeAdjustments';
import { getOutcomeBadge, type OutcomePattern } from '@/lib/enterprise/outcomePatterns';
import type { RecurrenceEntry } from '@/lib/enterprise/compositePatterns';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertTriangle, TrendingDown, Clock, Scale, Store,
  Zap, CheckCircle, ChevronDown, ChevronRight, Activity,
  Bell, BellOff, Brain, RefreshCw, Eye,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { NetworkSignal } from '@/hooks/useNetworkSignals';

interface SignalPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  signals: NetworkSignal[];
  onAcknowledge: (id: string) => Promise<void>;
  onDismiss: (id: string) => Promise<void>;
  isLoading: boolean;
  patterns?: LearningPattern[];
  learningEnabled?: boolean;
  recurrence?: RecurrenceEntry[];
  outcomes?: OutcomePattern[];
}

function getSeverityBadge(severity: NetworkSignal['severity']) {
  switch (severity) {
    case 'high': return 'bg-destructive/20 text-destructive border-0';
    case 'medium': return 'bg-warning/20 text-warning border-0';
    case 'low': return 'bg-blue-500/20 text-blue-600 border-0';
  }
}

function getTypeIcon(type: string) {
  switch (type) {
    case 'OVERLOAD_ATTENTION': return <AlertTriangle className="h-4 w-4" />;
    case 'UNDERUTILIZATION': return <TrendingDown className="h-4 w-4" />;
    case 'COMPLETION_WEAKNESS': return <TrendingDown className="h-4 w-4" />;
    case 'WAITER_PRESSURE': return <Clock className="h-4 w-4" />;
    case 'CAPACITY_IMBALANCE': return <Scale className="h-4 w-4" />;
    default: return <Activity className="h-4 w-4" />;
  }
}

function formatTimestamp(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
    ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export function SignalPanel({ open, onOpenChange, signals, onAcknowledge, onDismiss, isLoading, patterns = [], learningEnabled = false, recurrence = [], outcomes = [] }: SignalPanelProps) {
  const navigate = useNavigate();
  const { setActiveShop } = useAuth();
  const { recordEvent } = useNetworkLearning();
  const [showAcknowledged, setShowAcknowledged] = useState(false);

  // Sort with learning + outcome adjustment when enabled
  const sortWithLearning = (a: NetworkSignal, b: NetworkSignal) => {
    const learnA = getSignalLearningAdjustment(a.signal_type, patterns, learningEnabled).adjustment;
    const learnB = getSignalLearningAdjustment(b.signal_type, patterns, learningEnabled).adjustment;
    const outcomeA = learningEnabled ? computeOutcomeAdjustment(a.signal_type, outcomes).adjustment : 0;
    const outcomeB = learningEnabled ? computeOutcomeAdjustment(b.signal_type, outcomes).adjustment : 0;
    return (b.priority_score + learnB + outcomeB) - (a.priority_score + learnA + outcomeA);
  };

  const activeSignals = signals
    .filter((s) => s.status === 'active')
    .sort(sortWithLearning);

  const acknowledgedSignals = signals
    .filter((s) => s.status === 'acknowledged')
    .sort(sortWithLearning);

  const handleAcknowledge = async (signal: NetworkSignal) => {
    recordEvent('signal', signal.id, 'signal_acknowledged', signal.shop_id, { signal_type: signal.signal_type, severity: signal.severity });
    await onAcknowledge(signal.id);
  };

  const handleDismiss = async (signal: NetworkSignal) => {
    recordEvent('signal', signal.id, 'signal_dismissed', signal.shop_id, { signal_type: signal.signal_type, severity: signal.severity });
    await onDismiss(signal.id);
  };

  const handleViewShop = async (signal: NetworkSignal) => {
    recordEvent('signal', signal.id, 'signal_view_shop', signal.shop_id, { signal_type: signal.signal_type });
    await setActiveShop(signal.shop_id!);
    onOpenChange(false);
    navigate('/');
  };

  const handleViewIntelligence = (signal: NetworkSignal) => {
    recordEvent('signal', signal.id, 'signal_view_intelligence', signal.shop_id, { signal_type: signal.signal_type });
    onOpenChange(false);
    navigate('/org/intelligence');
  };

  // Helper to check signal recurrence
  const getRecurrenceCount = (signal: NetworkSignal): number => {
    if (!signal.shop_id) return 0;
    const entry = recurrence.find(
      (r) => r.signal_type === signal.signal_type && r.shop_id === signal.shop_id,
    );
    return entry?.occurrence_count ?? 0;
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader className="pb-4 border-b">
          <SheetTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            Network Signals
            {activeSignals.length > 0 && (
              <span className="bg-destructive/20 text-destructive text-xs px-2 py-0.5 rounded-full font-semibold">
                {activeSignals.length}
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-120px)] pr-4 -mr-4">
          <div className="space-y-4 py-4">
            {isLoading ? (
              <div className="text-center text-muted-foreground py-8">Loading signals…</div>
            ) : activeSignals.length === 0 && acknowledgedSignals.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <BellOff className="h-12 w-12 text-muted-foreground mb-3" />
                <p className="font-medium">No Active Signals</p>
                <p className="text-sm text-muted-foreground mt-1">
                  All shops within your scope appear balanced.
                </p>
              </div>
            ) : (
              <>
                {/* Active Signals */}
                {activeSignals.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-foreground">Active</h3>
                    {activeSignals.map((signal) => (
                      <SignalCard
                        key={signal.id}
                        signal={signal}
                        onAcknowledge={() => handleAcknowledge(signal)}
                        onDismiss={() => handleDismiss(signal)}
                        onViewShop={signal.shop_id ? () => handleViewShop(signal) : undefined}
                        onViewIntelligence={!signal.shop_id ? () => handleViewIntelligence(signal) : undefined}
                        learningAdjusted={learningEnabled && getSignalLearningAdjustment(signal.signal_type, patterns, true).adjustment !== 0}
                        recurrenceCount={getRecurrenceCount(signal)}
                        outcomes={outcomes}
                      />
                    ))}
                  </div>
                )}

                {/* Acknowledged Signals */}
                {acknowledgedSignals.length > 0 && (
                  <div className="space-y-3">
                    <button
                      className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => setShowAcknowledged(!showAcknowledged)}
                    >
                      {showAcknowledged ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      Acknowledged ({acknowledgedSignals.length})
                    </button>
                    {showAcknowledged && acknowledgedSignals.map((signal) => (
                      <SignalCard
                        key={signal.id}
                        signal={signal}
                        onViewShop={signal.shop_id ? () => handleViewShop(signal) : undefined}
                        onViewIntelligence={!signal.shop_id ? () => handleViewIntelligence(signal) : undefined}
                        muted
                        recurrenceCount={getRecurrenceCount(signal)}
                        outcomes={outcomes}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

// ── Signal Card ─────────────────────────────────────

function SignalCard({
  signal,
  onAcknowledge,
  onDismiss,
  onViewShop,
  onViewIntelligence,
  muted = false,
  learningAdjusted = false,
  recurrenceCount = 0,
  outcomes = [],
}: {
  signal: NetworkSignal;
  onAcknowledge?: () => void;
  onDismiss?: () => void;
  onViewShop?: () => void;
  onViewIntelligence?: () => void;
  muted?: boolean;
  learningAdjusted?: boolean;
  recurrenceCount?: number;
  outcomes?: OutcomePattern[];
}) {
  const outcomeBadge = getOutcomeBadge(signal.signal_type, outcomes);
  return (
    <Card className={cn('border', muted && 'opacity-60')}>
      <CardContent className="p-3 space-y-2">
        <div className="flex items-start gap-2">
          <div className="mt-0.5 text-muted-foreground">
            {getTypeIcon(signal.signal_type)}
          </div>
          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm text-foreground truncate">{signal.title}</span>
              <Badge className={cn(getSeverityBadge(signal.severity), 'text-xs')}>
                {signal.severity === 'high' ? 'High' : signal.severity === 'medium' ? 'Medium' : 'Low'}
              </Badge>
              {learningAdjusted && (
                <span className="flex items-center gap-0.5 text-[10px] text-primary">
                  <Brain className="h-3 w-3" />
                </span>
              )}
              {recurrenceCount >= 3 && (
                <Badge variant="outline" className="text-[10px] gap-0.5 border-warning/30 text-warning px-1.5 py-0">
                  <RefreshCw className="h-2.5 w-2.5" />
                  {recurrenceCount}x
                </Badge>
              )}
              {outcomeBadge && (
                <Badge variant="outline" className={cn('text-[10px] gap-0.5 px-1.5 py-0', outcomeBadge.className)}>
                  {outcomeBadge.label}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{signal.summary}</p>

            {/* Reasons */}
            <div className="space-y-0.5">
              {signal.reasons.map((reason, i) => (
                <div key={i} className="flex items-center gap-1.5 text-xs text-foreground">
                  <Activity className="h-3 w-3 text-muted-foreground shrink-0" />
                  <span>{reason}</span>
                </div>
              ))}
            </div>

            {/* Timestamps */}
            <p className="text-xs text-muted-foreground">
              First seen: {formatTimestamp(signal.first_detected_at)}
              {signal.last_detected_at !== signal.first_detected_at && (
                <> · Last: {formatTimestamp(signal.last_detected_at)}</>
              )}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 ml-6 flex-wrap">
          {onAcknowledge && signal.status === 'active' && (
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={onAcknowledge}>
              <CheckCircle className="h-3 w-3" />
              Acknowledge
            </Button>
          )}
          {onDismiss && signal.status === 'active' && (
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onDismiss}>
              Dismiss
            </Button>
          )}
          {onViewShop && (
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={onViewShop}>
              <Store className="h-3 w-3" />
              View Shop
            </Button>
          )}
          {onViewIntelligence && (
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={onViewIntelligence}>
              <Zap className="h-3 w-3" />
              Intelligence
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
